import { Box, TableCell, TableRow, Typography } from "@mui/material";
import { useLegalEntityAuditClassificationReport } from "./LegalEntityAuditClassificationReport.hooks.ts";
import { useTranslation } from "../../localization/useTranslation.ts";
import {
    DotLegalButton,
    DotLegalFullScreenSpinner,
    DotLegalHeader,
    DotLegalMultiSelect,
    DotLegalPdfBackPage,
    DotLegalPdfFrontPage,
    DotLegalTableWithControls,
    GridSizes,
    ITableHeader,
} from "@dotlegal/dotlegal-ui-components";
import React from "react";
import {
    LegalEntityRiskAuditClassificationReportRow,
    LegalEntityRiskAuditClassificationReportRowTableModel,
} from "./LegalEntityAuditClassificationReport.types.ts";
import Grid from "@mui/material/Grid2";
import { getQuestionnaireResponseScore } from "../../common/questionnaireResponseScore.ts";
import DotLegalLink from "../../common/components/dotLegalLink/DotLegalLink.tsx";
import SystemLinks from "../../system/SystemLinks.tsx";
import QuestionnaireEvaluation from "../../common/components/questionnaireComponents/questionnaireEvaluation/QuestionnaireEvaluation.tsx";
import MentionInputRender from "../../common/components/mentionInput/MentionInputRender.tsx";
import { useUrlProvider } from "../../useUrlProvider.ts";
import RiskLevelChart from "@common/components/managementReport/managementReportCharts/allocationOfRiskLevelChart/riskLevelChart/RiskLevelChart.tsx";
import { IDownloadAuditReportModel } from "../../legalEntityAuditGroup/DowloadLegalEntityAuditReport.hooks.ts";
import QuestionnaireReportButtons from "../../common/components/questionnaireComponents/questionnaireReportButtons/QuestionnaireReportButtons.tsx";
import QuestionnaireReportOverflowButtons from "../../common/components/questionnaireComponents/questionnaireReportButtons/QuestionnaireReportOverflowButtons.tsx";
import { useSetPdfPageSize } from "../../common/pdfPageSizeHelper.ts";
import { RiskLevel } from "../legalEntityClassificationTab/LegalEntityClassificationTab.types.ts";
import { warning } from "../../common/icons.tsx";
import { useRiskIndicator } from "../../riskAssessment/riskIndicator/RiskIndicator.hooks.ts";
import { LegalEntityAuditTabs } from "../LegalEntity.types.ts";

export interface ILegalEntityAuditClassificationReportProps {
    type: "audit" | "classification";
}

function LegalEntityAuditClassificationReport(props: ILegalEntityAuditClassificationReportProps) {
    const { translateString, translateDate } = useTranslation();
    const {
        data,
        rows,
        isLoading,
        isPdf,
        searchedRiskLevel,
        setSearchedRiskLevel,
        riskLevelFilterOptions,
        searchedScore,
        setSearchedScore,
        scoreFilterOptions,
        onDownloadAuditReportAsPdf,
        getAuditReportPdfUrl,
        isDownloadingAuditPdf,
        onDownloadClassificationReportAsPdf,
        getClassificationReportPdfUrl,
        isDownloadingClassificationPdf,
        downloadReport,
        isDownloadingPdf,
    } = useLegalEntityAuditClassificationReport(props);
    const { getLegalEntityUrl } = useUrlProvider();
    const isClassification = props.type === "classification";
    const tableSectionHeader = isClassification ? translateString("overviewOfIndividualClassifications") : translateString("overviewOfIndividualAudits");
    const reportHeader = isClassification ? translateString("legalEntityRiskClassificationReport") : translateString("legalEntityRiskAuditReport");
    const tableReportHeader = isClassification ? translateString("classificationReport") : translateString("legalEntityAuditReport");
    const infoText = isClassification ? translateString("legalEntityRiskClassificationReportInfoText") : translateString("legalEntityRiskAuditReportInfoText");
    const chartInfoText = isClassification
        ? translateString("legalEntityRiskClassificationReportChartInfoText")
        : translateString("legalEntityRiskAuditReportChartInfoText");
    const scoreEnabled = data?.rows.some((item) => item.scoreEnabled);
    const { getRiskIcon } = useRiskIndicator();
    useSetPdfPageSize("landscape");

    function getHeaders() {
        let headers = Array<ITableHeader<LegalEntityRiskAuditClassificationReportRowTableModel>>();
        headers.push({ property: "legalEntityName", text: translateString("legalEntities"), align: "left", showOnMobile: true });
        headers.push({ property: "assets", text: translateString("assets"), align: "left", showOnMobile: true });
        if (scoreEnabled) {
            headers.push({ property: "riskLevel", text: translateString("riskLevel"), align: "left", showOnMobile: true });
            headers.push({ property: "score", text: translateString("score"), align: "left", showOnMobile: true });
        }
        headers.push({ property: "submittedDate", text: translateString("submitted"), align: "left", showOnMobile: true });
        headers.push({ property: "submittedBy", text: translateString("submittedBy"), align: "left", showOnMobile: true });
        headers.push({ property: "evaluationStatus", text: translateString("evaluation"), align: "left", showOnMobile: true });

        if (isPdf) {
            headers.push({ property: "evaluation", text: translateString("evaluationText"), align: "left", showOnMobile: true });
        }

        headers.push({ property: "id", text: tableReportHeader, align: "left", showOnMobile: true });
        headers.push({ property: "note", text: translateString("note"), align: "left", showOnMobile: true });

        // Hidden headers - allowing search
        headers.push({ property: "assetsString", text: "", align: "left", showOnMobile: true, hideHeader: true });

        return headers;
    }

    function getSearchFields() {
        const gridSizes: GridSizes = {
            xs: 12,
            sm: 6,
            md: 4,
            lg: 3,
        };

        if (!rows.some((x) => x.scoreEnabled)) {
            return undefined;
        }

        return (
            <React.Fragment>
                <Grid size={{ ...gridSizes }}>
                    <DotLegalMultiSelect
                        onChange={setSearchedRiskLevel}
                        chosenOptions={searchedRiskLevel}
                        label={translateString("riskLevel")}
                        placeholder={translateString("riskLevel")}
                        isLoading={isLoading}
                        options={riskLevelFilterOptions}
                        noMargin
                        limit={1}
                        noOptionsLabel={translateString("noOptions")}
                    />
                </Grid>
                <Grid size={{ ...gridSizes }}>
                    <DotLegalMultiSelect
                        onChange={setSearchedScore}
                        chosenOptions={searchedScore}
                        label={translateString("score")}
                        placeholder={translateString("score")}
                        isLoading={isLoading}
                        options={scoreFilterOptions}
                        noMargin
                        limit={1}
                        noOptionsLabel={translateString("noOptions")}
                    />
                </Grid>
            </React.Fragment>
        );
    }

    function getReportButtons(row: LegalEntityRiskAuditClassificationReportRowTableModel) {
        if (isClassification) return getReportButtonsForClassification(row);
        return getReportButtonsForAudit(row);
    }

    function getReportButtonsForClassification(row: LegalEntityRiskAuditClassificationReportRowTableModel) {
        if (!data) return undefined;

        const viewModel = {
            id: row.id,
            questionnaireName: data.questionnaireName,
            legalEntityName: row.legalEntityName,
            questionnaireResponseId: row.questionnaireResponseId,
        };

        if (row.containsRecommendations) {
            return (
                <QuestionnaireReportOverflowButtons
                    reportPdfUrl={getClassificationReportPdfUrl(viewModel, false, true)}
                    reportPdfMenuItems={[
                        { menuItem: translateString("downloadWithRecommendations"), onClick: () => onDownloadClassificationReportAsPdf(viewModel, true) },
                        {
                            menuItem: translateString("downloadWithoutRecommendations"),
                            onClick: () => onDownloadClassificationReportAsPdf(viewModel, false),
                        },
                    ]}
                    showButtons
                    isDownloadingPdf={isDownloadingClassificationPdf(row.id)}
                />
            );
        }

        return (
            <QuestionnaireReportButtons
                showButtons
                reportPdfUrl={getClassificationReportPdfUrl(viewModel, false, false)}
                onDownloadReportPdf={() => onDownloadClassificationReportAsPdf(viewModel, false)}
                isDownloadingPdf={isDownloadingClassificationPdf(row.id)}
            />
        );
    }

    function getReportButtonsForAudit(row: LegalEntityRiskAuditClassificationReportRowTableModel) {
        if (!data) return undefined;

        const downloadModel: IDownloadAuditReportModel = {
            legalEntityAuditId: row.id,
            deadline: row.deadline!,
            legalEntityName: row.legalEntityName,
            questionnaireName: data.questionnaireName,
            questionnaireResponseId: row.questionnaireResponseId,
            responseExternalId: row.responseExternalId!,
            sender: row.sender!,
            hasRecommendations: row.containsRecommendations,
        };
        const reportUrl = getAuditReportPdfUrl(
            downloadModel.questionnaireResponseId!,
            downloadModel.deadline!,
            downloadModel.sender!,
            downloadModel.legalEntityName,
            false,
            downloadModel.hasRecommendations
        );
        const downloadingPdf = isDownloadingAuditPdf(row.id);

        if (row.scoreEnabled || row.containsRecommendations) {
            return (
                <QuestionnaireReportOverflowButtons
                    reportPdfUrl={reportUrl}
                    reportPdfMenuItems={getReportButtonMenuForAuditItems(row.scoreEnabled, downloadModel)}
                    showButtons
                    isDownloadingPdf={downloadingPdf}
                />
            );
        }

        return (
            <QuestionnaireReportButtons
                isDownloadingPdf={downloadingPdf}
                onDownloadReportPdf={() => onDownloadAuditReportAsPdf(downloadModel, true, row.containsRecommendations)}
                reportPdfUrl={reportUrl}
                showButtons
            />
        );
    }

    function getReportButtonMenuForAuditItems(isScoreEnabled: boolean, row: IDownloadAuditReportModel) {
        if (isScoreEnabled) {
            return [
                {
                    menuItem: row.hasRecommendations ? translateString("downloadWithScoreAndRecommendations") : translateString("downloadWithScore"),
                    onClick: () => onDownloadAuditReportAsPdf(row, false, row.hasRecommendations),
                },
                {
                    menuItem: row.hasRecommendations ? translateString("downloadWithoutScoreAndRecommendations") : translateString("downloadWithoutScore"),
                    onClick: () => onDownloadAuditReportAsPdf(row, true, false),
                },
            ];
        } else {
            return [
                { menuItem: translateString("downloadWithRecommendations"), onClick: () => onDownloadAuditReportAsPdf(row, false, true) },
                {
                    menuItem: translateString("downloadWithoutRecommendations"),
                    onClick: () => onDownloadAuditReportAsPdf(row, false, false),
                },
            ];
        }
    }

    function mapToTableData(
        items: Array<LegalEntityRiskAuditClassificationReportRow> | undefined
    ): Array<LegalEntityRiskAuditClassificationReportRowTableModel> {
        if (items) {
            return items.map((x) => {
                return {
                    ...x,
                    assetsString: x.assets.flatMap((x) => x.name).join(", "),
                    scoreString: getQuestionnaireResponseScore(true, x.scoreEnabled, x.score, translateString("scoreNotEnabled")),
                };
            });
        }

        return [];
    }

    function getRisk(row: LegalEntityRiskAuditClassificationReportRow) {
        var icon = getIcon(row);
        var name = getRiskName(row);

        return (
            <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                {icon}
                <Box>{name}</Box>
            </Box>
        );
    }

    function getIcon(row: LegalEntityRiskAuditClassificationReportRow) {
        if (row.riskLevel || row.riskLevel === 0) {
            return getRiskIcon(row.riskLevel);
        }

        if (!row.scoreEnabled) return getRiskIcon(RiskLevel.None);

        return warning;
    }

    function getRiskName(row: LegalEntityRiskAuditClassificationReportRow) {
        if (row.riskName) {
            return row.riskName;
        }

        if (!row.scoreEnabled) return translateString("none");

        return translateString("answerClassification");
    }

    function getYAxisTicks() {
        if (!data) return undefined;
        const maxNumber = Math.max(...data!.chartData.map((x) => x.count));
        if (maxNumber > 100) return undefined; //returning undefined will fallback to the default graph ticks
        let ticks: Array<string> = [];

        if (maxNumber <= 5) ticks = ["0", "1", "2", "3", "4", "5"];
        else if (maxNumber <= 10) ticks = ["0", "2", "4", "6", "8", "10"];
        else if (maxNumber <= 20) ticks = ["0", "4", "8", "12", "16", "20"];
        else if (maxNumber <= 25) ticks = ["0", "5", "10", "15", "20", "25"];
        else if (maxNumber <= 30) ticks = ["0", "6", "12", "18", "24", "30"];
        else if (maxNumber <= 35) ticks = ["0", "7", "14", "21", "28", "35"];
        else if (maxNumber <= 40) ticks = ["0", "8", "16", "24", "32", "40"];
        else if (maxNumber <= 50) ticks = ["0", "10", "20", "30", "40", "50"];
        else if (maxNumber <= 75) ticks = ["0", "15", "30", "45", "60", "75"];
        else if (maxNumber <= 100) ticks = ["0", "20", "40", "60", "80", "100"];

        return ticks.filter((x) => Number(x) <= maxNumber); //To avoid topmargin issues
    }

    return isLoading || data === undefined ? (
        <DotLegalFullScreenSpinner />
    ) : (
        <Box
            sx={{
                display: "flex",
                justifyContent: "center",
                width: "100%",
                color: "primary.dark",
                backgroundColor: isPdf ? "white" : "#fafafa",
            }}
        >
            <Box
                sx={(theme) => ({
                    width: "100vw",
                    maxWidth: "1300px",
                    [theme.breakpoints.down("sm")]: {
                        maxWidth: "100%",
                    },
                })}
            >
                <DotLegalPdfFrontPage header={data?.questionnaireName ?? ""} subHeader={reportHeader} isPdf={isPdf} backgroundImage={"blue"}>
                    <Box sx={{ marginTop: 9 }}>
                        <Typography sx={{ fontSize: 25, mt: 2 }}>
                            <Box component={"span"} sx={{ fontWeight: 700 }}>
                                {translateString("date")}: &nbsp;
                            </Box>
                            {translateDate(data.dateOfReport)}
                        </Typography>
                        {!isPdf && (
                            <Box sx={{ marginTop: 4 }}>
                                <DotLegalButton isLoading={isDownloadingPdf} buttonType={"primary"} onClick={() => downloadReport(reportHeader)}>
                                    {translateString("downloadReport")}
                                </DotLegalButton>
                            </Box>
                        )}
                    </Box>
                </DotLegalPdfFrontPage>

                <Box p={4} pt={isPdf ? 1 : 4} mb={isPdf ? 0 : 3} sx={{ backgroundColor: "white" }}>
                    <Box>
                        <DotLegalHeader headerStyle="xxl">{reportHeader}</DotLegalHeader>
                        {infoText}
                    </Box>

                    {scoreEnabled && (
                        <Box sx={{ display: isPdf ? "flex" : "block", justifyContent: "center", alignContent: "center" }}>
                            <RiskLevelChart
                                width={"100%"}
                                chartHeader={
                                    <>
                                        <DotLegalHeader marginBottom={0} headerStyle={"small"}>
                                            {`${translateString("figure")} 1`}:
                                        </DotLegalHeader>
                                        <Typography>{chartInfoText}</Typography>
                                    </>
                                }
                                isPdf={isPdf}
                                chartData={data.chartData}
                                shouldDisplayHasWeightOfZero={false}
                                bottomAxisLegendText={translateString("riskLevel")}
                                yAxisTicks={getYAxisTicks()}
                            />
                        </Box>
                    )}

                    <Box className="page-break-before">
                        <DotLegalHeader marginTop={isPdf ? 1 : 4} marginBottom={0} headerStyle="xxxl">
                            {tableSectionHeader}
                        </DotLegalHeader>
                        <Box sx={{ marginTop: 3 }} className={"avoid-page-break"}>
                            <DotLegalHeader headerStyle={"medium"} marginBottom={scoreEnabled ? 2 : isPdf ? 0 : -3.75}>
                                {translateString("completed").toUpperCase()}{" "}
                                <Box sx={(theme) => ({ color: theme.palette.grey[500] })} component="span">
                                    ({rows.length})
                                </Box>
                            </DotLegalHeader>
                            <DotLegalTableWithControls
                                getUserSpecificPageLength={() => 1000}
                                extraControls={getSearchFields()}
                                labelRowsPerPage={""}
                                labelSearch={translateString("search")}
                                noOptionsLabel={translateString("noOptions")}
                                headers={getHeaders()}
                                defaultOrderBy={"legalEntityName"}
                                defaultOrder={"asc"}
                                paginationLabelOf={""}
                                hidePagination
                                hideRowsPerPage
                                data={mapToTableData(rows)}
                                emptyText={translateString("noData")}
                                renderRow={(row) => {
                                    return (
                                        <TableRow key={row.legalEntityId}>
                                            <TableCell>
                                                <Box key={row.id} component={"span"} sx={{ fontWeight: "bold" }}>
                                                    <DotLegalLink
                                                        linkColor={"primary"}
                                                        to={`${getLegalEntityUrl(row.legalEntityId, isClassification ? LegalEntityAuditTabs.Classifications : LegalEntityAuditTabs.Audits)}`}
                                                    >
                                                        {row.legalEntityName}
                                                    </DotLegalLink>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <SystemLinks systems={row.assets} />
                                            </TableCell>
                                            {scoreEnabled && (
                                                <>
                                                    <TableCell>{getRisk(row)}</TableCell>
                                                    <TableCell align="right">{row.scoreString}</TableCell>
                                                </>
                                            )}
                                            <TableCell>{translateDate(row.submittedDate)}</TableCell>
                                            <TableCell>{row.submittedBy}</TableCell>
                                            <TableCell>
                                                <QuestionnaireEvaluation evaluationStatus={row.evaluationStatus} evaluation={row.evaluation} />
                                            </TableCell>
                                            {isPdf && <TableCell>{row.evaluation}</TableCell>}
                                            <TableCell>{getReportButtons(row)}</TableCell>
                                            <TableCell></TableCell>
                                            <TableCell>
                                                <MentionInputRender value={row.note ?? ""} />
                                            </TableCell>
                                        </TableRow>
                                    );
                                }}
                            />
                        </Box>
                    </Box>
                </Box>

                {isPdf && (
                    <Box sx={{ backgroundColor: "white" }} className={"page-break-before"}>
                        <DotLegalPdfBackPage />
                    </Box>
                )}
            </Box>
        </Box>
    );
}

export default LegalEntityAuditClassificationReport;
